import AxiosHttpClient from 'app/api/AxiosHttpClient';
import { Reference } from 'app/model/Reference';
import { Page } from 'app/model/Page';
import { LocalityReference } from 'app/model/LocalityReference';
import { StreetReference } from 'app/model/StreetReference';
import { EmptyReferenceException } from 'app/api/exception/EmptyReferenceException';

/**
 * Сервис работы со справочниками
 */
export class ReferenceService {
    private readonly EMPLOYMENTS_REFERENCE = '/reference/employments';

    private readonly GET_EMPLOYMENT_BY_ID = (id: string) =>
        `reference/employments/${id}`;

    private readonly EXPERIENCES_REFERENCE = '/reference/experiences';

    private readonly GET_EXPERIENCE_BY_ID = (id: string) =>
        `/reference/experiences/${id}`;

    private readonly INDUSTRIES_REFERENCE = '/reference/industries';

    private readonly GET_INDUSTRY_BY_ID = (id: string) =>
        `/reference/industries/${id}`;

    private readonly DOCUMENT_TYPES_REFERENCE = '/reference/document-types';

    private readonly GET_DOCUMENT_TYPE_BY_ID = (id: string) =>
        `/reference/document-types/${id}`;

    private readonly DOCUMENT_ISSUES_REFERENCE = '/reference/document-issuers';

    private readonly GET_DOCUMENT_ISSUE_BY_ID = (id: string) =>
        `/reference/document-issuers/${id}`;

    private readonly COUNTRIES_REFERENCE = '/reference/countries';

    private readonly GET_COUNTRY_BY_ID = (id: string) =>
        `/reference/countries/${id}`;

    private readonly LOCALITY_TYPES_REFERENCE = '/reference/locality-types';

    private readonly GET_LOCALITY_TYPE_BY_ID = (id: string) =>
        `/reference/locality-types/${id}`;

    private readonly LOCALITIES_REFERENCE = '/reference/localities';

    private readonly GET_LOCALITY_BY_ID = (id: string) =>
        `/reference/localities/${id}`;

    private readonly STREET_TYPES_REFERENCE = '/reference/street-types';

    private readonly GET_STREET_TYPE_BY_ID = (id: string) =>
        `/reference/street-types/${id}`;

    private readonly STREETS_REFERENCE = '/reference/streets';

    private readonly GET_STREET_BY_ID = (id: string) =>
        `/reference/streets/${id}`;

    private apiClient: AxiosHttpClient = new AxiosHttpClient();

    /**
     * Получить данные справочника видов деятельности.
     */
    async getEmploymentsReference(): Promise<Array<Reference>> {
        return this.handleArrayResponse(
            await this.apiClient.executeGetRequest(
                this.EMPLOYMENTS_REFERENCE,
                Page.first(),
            ),
        );
    }

    /**
     * Получить по id вид деятельности.
     * @param id идентификатор значения справочника
     */
    async getEmploymentsById(id: string): Promise<Reference | null> {
        return this.orElseNull(() => !!id)(() =>
            this.apiClient.executeGetRequest(this.GET_EMPLOYMENT_BY_ID(id)),
        );
    }

    /**
     * Получить данные справочника видов стажа.
     */
    async getExperiencesReference(): Promise<Array<Reference>> {
        return this.handleArrayResponse(
            await this.apiClient.executeGetRequest(
                this.EXPERIENCES_REFERENCE,
                Page.first(),
            ),
        );
    }

    /**
     * Получить по id вид стажа.
     * @param id идентификатор значения справочника
     */
    async getExperienceById(id: string): Promise<Reference | null> {
        return this.orElseNull(() => !!id)(() =>
            this.apiClient.executeGetRequest(this.GET_EXPERIENCE_BY_ID(id)),
        );
    }

    /**
     * Получить данные справочника справочник сфер деятельности.
     */
    async getIndustriesReference(): Promise<Array<Reference>> {
        return this.handleArrayResponse(
            await this.apiClient.executeGetRequest(
                this.INDUSTRIES_REFERENCE,
                Page.first(),
            ),
        );
    }

    /**
     * Получить по id сферу деятельности.
     * @param id идентификатор значения справочника
     */
    async getIndustryById(id: string): Promise<Reference | null> {
        return this.orElseNull(() => !!id)(() =>
            this.apiClient.executeGetRequest(this.GET_INDUSTRY_BY_ID(id)),
        );
    }

    /**
     * Получить данные справочника типов документов, удостоверяющих личность.
     */
    async getDocumentTypesReference(): Promise<Array<Reference>> {
        return this.apiClient.executeGetRequest(
            this.DOCUMENT_TYPES_REFERENCE,
            Page.first(),
        );
    }

    /**
     * Получить по id тип документа, удостоверяющего личность.
     * @param id идентификатор значения справочника
     */
    async getDocumentTypeById(id: string): Promise<Reference | null> {
        return this.orElseNull(() => !!id)(() =>
            this.apiClient.executeGetRequest(this.GET_DOCUMENT_TYPE_BY_ID(id)),
        );
    }

    /**
     * Получить данные справочника органов, выдающих документ, удостоверяющий личность.
     */
    async getDocumentIssuersReference(): Promise<Array<Reference>> {
        return this.apiClient.executeGetRequest(
            this.DOCUMENT_ISSUES_REFERENCE,
            Page.first(),
        );
    }

    /**
     * Получить по id орган, выдающий документы, удостоверяющие личность.
     * @param id идентификатор значения справочника
     */
    async getDocumentIssuerById(id: string): Promise<Reference | null> {
        return this.orElseNull(() => !!id)(() =>
            this.apiClient.executeGetRequest(this.GET_DOCUMENT_ISSUE_BY_ID(id)),
        );
    }

    /**
     * Получить список стран отфильтрованных по наименованию
     * @param value значение фильра
     */
    async getCountriesReference(value: string): Promise<Array<Reference>> {
        return this.apiClient.executeGetRequest(this.COUNTRIES_REFERENCE, {
            filter: value,
        });
    }

    /**
     * Получить страну в формате {@link Reference} по идентификатору
     * @param id идентификатор значения справочника
     */
    async getCountryById(id: string): Promise<Reference | null> {
        return this.orElseNull(() => !!id)(() =>
            this.apiClient.executeGetRequest(this.GET_COUNTRY_BY_ID(id)),
        );
    }

    /**
     * Получить список типов населенных пунктов отфильтрованных по наименованию
     * @param value значение фильтра
     */
    async getLocalityTypesReference(value?: string): Promise<Array<Reference>> {
        return this.handleArrayResponse(
            await this.apiClient.executeGetRequest(
                this.LOCALITY_TYPES_REFERENCE,
                { filter: value },
            ),
        );
    }

    /**
     * Получить тип населённого пункта по идентификатору
     * @param id идентификатор значения справочника
     */
    async getLocalityTypeById(id: string): Promise<Reference | null> {
        return this.orElseNull(() => !!id)(() =>
            this.apiClient.executeGetRequest(this.GET_LOCALITY_TYPE_BY_ID(id)),
        );
    }

    /**
     * Получить список населенных пунктов отфильтрованных по наименованию
     * @param value значение фильтра
     */
    async getLocalitiesReference(
        value: string,
    ): Promise<Array<LocalityReference>> {
        return this.apiClient.executeGetRequest(this.LOCALITIES_REFERENCE, {
            filter: value,
        });
    }

    /**
     * Получить значение справочника "Населённый пункт" по коду soato
     * @param soatoCode
     */
    async getLocalityBySoatoCode(
        soatoCode: string,
    ): Promise<LocalityReference | null> {
        return this.orElseNull(() => !!soatoCode)(() =>
            this.apiClient.executeGetRequest(
                this.GET_LOCALITY_BY_ID(soatoCode),
            ),
        );
    }

    /**
     * Получить населённый пункт по идентификатору
     * @param id идентификатор значения справочника
     */
    async getLocalityById(id: string): Promise<LocalityReference | null> {
        return this.orElseNull(() => !!id)(() =>
            this.apiClient.executeGetRequest(this.GET_LOCALITY_BY_ID(id)),
        );
    }

    /**
     * Получить список типов улиц отфильтрованных по наименованию
     * @param value значение фильтра
     */
    async getStreetTypesReference(value?: string): Promise<Array<Reference>> {
        return this.apiClient.executeGetRequest(this.STREET_TYPES_REFERENCE, {
            filter: value,
        });
    }

    /**
     * Получить тип улиц по идентификатору
     * @param id идентификатор значения справочника
     */
    async getStreetTypeById(id: string): Promise<Reference | null> {
        return this.orElseNull(() => !!id)(() =>
            this.apiClient.executeGetRequest(this.GET_STREET_TYPE_BY_ID(id)),
        );
    }

    /**
     * Получить список улиц отфильтрованных по наименованию
     * @param value значение фильтра
     * @param soato значение кода СОАТО выбранного населённого пункта
     */
    async getStreetsReference(
        value: string,
        soato: string,
    ): Promise<Array<StreetReference> | null> {
        return this.orElseNull(() => !!value && !!soato)(async () => {
            const streetReferences = await this.apiClient.executeGetRequest(
                this.STREETS_REFERENCE,
                { filter: value, soato },
            );
            return streetReferences.map(StreetReference.deserialize);
        });
    }

    /**
     * Возвращает @{Reference#empty} в случае если условие не выполнено
     * @param condition
     * @private
     */
    private orElseNull(condition: () => boolean) {
        return (asyncFunction: () => Promise<Reference>) => {
            if (condition()) {
                return asyncFunction();
            }

            return Promise.resolve(null);
        };
    }

    /**
     * Существует виды спрвочников для которых пустой список необходимо обрабатывать как ошибочная ситуация.
     * Метод выбрасывает исключение в случае если ответ от сервера содержит пустой список.
     * @private
     */
    private async handleArrayResponse(
        response: Array<Reference>,
    ): Promise<Array<Reference>> {
        if (response.length) {
            return response;
        } else {
            throw new EmptyReferenceException();
        }
    }
}
